import { useCallback, useMemo } from "react";
import { Stack } from "@altimateai/lego";
import { DataTable } from "@/components/tables";
import { Paginate } from "@/components/paginate";
import { FilterApplicableFor } from "@/components/tags/types";
import { useTablePagination } from "@/components/tables/hooks/useTablePagination";
import { useAppState } from "@/modules/app/useAppContext";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import type { UnifiedQuery, UnifiedQueriesResponse } from "../types";
import type { SortDirection } from "@/components/tables/types";
import { getQueryTableColumns, type QueryIdSearchFn } from "./queryTableColumns";
import { DEFAULT_QUERIES_SORT } from "./useDatabricksQueriesFilters";

interface FilterOption {
  label: string;
  value: string;
}

interface QueryTableProps {
  data: UnifiedQueriesResponse | undefined;
  isFetching: boolean;
  page: number;
  pageSize: number;
  sortAttr: string;
  sortOrder: SortDirection;
  statementTypeFilter: string;
  statementIdFilter: string[];
  /** Unified Compute column filter — selected ``{kind}:{id}`` values. */
  computeFilter: string[];
  statementTypeOptions: FilterOption[];
  /** Unified Compute dropdown options (warehouse + cluster + pipeline). */
  computeOptions: FilterOption[];
  /** Server-side search for the Query ID dropdown. */
  queryIdSearchFn: QueryIdSearchFn;
  /** Centralized sort writer from `useDatabricksQueriesFilters`. */
  onSortChange: (attribute: string, direction: SortDirection) => void;
}

const QueryTable = (props: QueryTableProps) => {
  const {
    data,
    isFetching,
    page,
    pageSize,
    sortAttr,
    sortOrder,
    statementTypeFilter,
    statementIdFilter,
    computeFilter,
    statementTypeOptions,
    computeOptions,
    queryIdSearchFn,
    onSortChange,
  } = props;
  const { currency } = useAppState();
  const { updateSearchParamsByKey } = useFiltersBySearchParams();
  const { setPage, setPageSize: updatePageSize } = useTablePagination();

  const handleMultiFilterChange = useCallback(
    (key: string, values: string[]) => {
      updateSearchParamsByKey({ [key]: values, page: "1" });
    },
    [updateSearchParamsByKey]
  );

  // Memoized so header cells don't reconcile every parent render.
  const columns = useMemo(
    () =>
      getQueryTableColumns({
        sortAttr,
        sortOrder,
        statementTypeFilter,
        statementIdFilter,
        computeFilter,
        statementTypeOptions,
        computeOptions,
        currency,
        queryIdSearchFn,
        onSortChange,
        onMultiFilterChange: handleMultiFilterChange,
      }),
    [
      sortAttr,
      sortOrder,
      statementTypeFilter,
      statementIdFilter,
      computeFilter,
      statementTypeOptions,
      computeOptions,
      currency,
      queryIdSearchFn,
      onSortChange,
      handleMultiFilterChange,
    ]
  );

  return (
    <Stack direction="column" gap="md">
      <div id="databricks-queries-saved-filters-container" />
      <DataTable
        idKey="statement_id"
        getRowUrl={(item: UnifiedQuery) => `/queries/${item.statement_id}`}
        header={columns}
        data={{ items: data?.queries || [] }}
        isLoading={isFetching}
        minWidth={1400}
        defaultSort={DEFAULT_QUERIES_SORT}
        applicableFor={FilterApplicableFor.DATABRICKS_QUERIES}
        portals={{
          filter: "databricks-queries-filter-container",
          savedFilters: "databricks-queries-saved-filters-container",
        }}
      />
      <Paginate
        itemCount={data?.total_count || 0}
        page={page}
        pageSize={pageSize}
        numPages={data?.total_pages || 1}
        onPageClick={setPage}
        onPageSizeChange={updatePageSize}
        showPageSize
        isLoading={isFetching}
      />
    </Stack>
  );
};

export { QueryTable };
